/**
 * Locale context. Vietnamese by default, English as a fallback-per-key frame.
 *
 * No i18n library. The catalogue is two objects and the only feature the product
 * needs is `{name}` interpolation, which is four lines. G2 fixes a minimal
 * dependency set for the server and the same reasoning holds here: a dependency
 * earns its place by doing something the alternative cannot, and ICU plurals,
 * lazy-loaded namespaces and a translation-management backend are not on the
 * requirement list of spec 01 §19.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { CATALOGUES, DEFAULT_LOCALE, LOCALES, vi, type Locale, type MessageKey } from './messages';

export type { Locale, MessageKey };
export { LOCALES, DEFAULT_LOCALE };

export type Translate = (
  key: MessageKey,
  vars?: Readonly<Record<string, string | number>>,
) => string;

export interface I18nValue {
  readonly locale: Locale;
  readonly setLocale: (locale: Locale) => void;
  readonly t: Translate;
}

const STORAGE_KEY = 'wp.locale';

/**
 * A **preference**, not a credential. SEC-25 bans tokens from `localStorage` and
 * that ban is honoured elsewhere in this module tree — the CSRF token lives in
 * memory only. A locale choice discloses nothing and surviving a reload is the
 * whole point of choosing it.
 */
function readStoredLocale(): Locale | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return LOCALES.find((candidate) => candidate === raw) ?? null;
  } catch {
    // Private mode, disabled storage, a test environment without it. A preference
    // that cannot be remembered is not an error worth surfacing.
    return null;
  }
}

function storeLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* see readStoredLocale */
  }
}

const INTERPOLATION = /\{([a-zA-Z0-9_]+)\}/g;

export function interpolate(
  template: string,
  vars: Readonly<Record<string, string | number>> | undefined,
): string {
  if (vars === undefined) return template;
  return template.replace(INTERPOLATION, (whole, name: string) => {
    const value = vars[name];
    // An unknown placeholder is left verbatim rather than replaced with an empty
    // string: `{count}` on screen is a visible bug report, and a silently missing
    // number reads as a real zero.
    return value === undefined ? whole : String(value);
  });
}

export function translate(
  locale: Locale,
  key: MessageKey,
  vars?: Readonly<Record<string, string | number>>,
): string {
  const catalogue = CATALOGUES[locale];
  const message = (catalogue as Partial<Record<MessageKey, string>>)[key] ?? vi[key];
  return interpolate(message, vars);
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({
  children,
  initialLocale,
}: {
  readonly children: ReactNode;
  readonly initialLocale?: Locale;
}): ReactNode {
  const [locale, setLocaleState] = useState<Locale>(
    () => initialLocale ?? readStoredLocale() ?? DEFAULT_LOCALE,
  );

  // `<html lang>` is what a screen reader picks a voice from, and Vietnamese read
  // in an English voice is close to unusable. Kept in step with the choice rather
  // than set once at boot.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    storeLocale(next);
  }, []);

  const t = useCallback<Translate>((key, vars) => translate(locale, key, vars), [locale]);

  const value = useMemo<I18nValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (value === null) {
    throw new Error('useI18n was called outside <I18nProvider>. Wrap the tree in one.');
  }
  return value;
}

/** Shorthand for the common case. */
export function useT(): Translate {
  return useI18n().t;
}
